import { CommonModule, NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  inject,
  NgModule,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { NgxsModule, select } from '@ngxs/store';
import { ToastrService } from 'ngx-toastr';
import { throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import {
  ContactState,
  ContactStateGetContactSubjects,
  ContactStateSubmitMessageLoggedIn,
  ContactStateSubmitMessageNotLoggedIn,
} from './contact.state';
import {
  AnimateModule,
  FormErrorHandlerService,
  GtmClickDirective,
  HeaderComponent,
  InputValidatorDirective,
  InvalidControlScrollDirective,
  ValidateMessageComponent,
} from '@pro-connect/shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BasicPage, WwwRouteSlug } from '../../../../models';
import { NgxMaskDirective } from 'ngx-mask';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import { Angulartics2GoogleTagManager } from 'angulartics2';
import { IContactSubject } from './models/contact-subject';

export type ContactLoggedInFormType = {
  message: FormControl<string | null>;
  subject: FormControl<number | null>;
};

export type ContactNotLoggedInFormType = {
  message: FormControl<string | null>;
  subject: FormControl<number | null>;
  saloonCode: FormControl<string | null>;
  firstname: FormControl<string | null>;
  lastname: FormControl<string | null>;
  phone: FormControl<string | null>;
  email: FormControl<string | null>;
  zipCode: FormControl<string | null>;
};

@NgModule({
  imports: [NgxsModule.forFeature([ContactState])],
})
class ContactStateLazyModule {}

@Component({
  templateUrl: './contact.component.html',
  styleUrls: ['./contact.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    AnimateModule,
    ReactiveFormsModule,
    NgxsFormPluginModule,
    ValidateMessageComponent,
    InputValidatorDirective,
    ContactStateLazyModule,
    InvalidControlScrollDirective,
    NgOptimizedImage,
    NgxMaskDirective,
    NgSelectComponent,
    NgOptionComponent,
    HeaderComponent,
    GtmClickDirective,
  ],
})
export class ContactComponent extends BasicPage implements OnInit {
  public readonly contactSubjects = select(ContactState.contactSubjects);
  public readonly loading = select(ContactState.loading);
  public readonly WwwRouteSlug = WwwRouteSlug;
  public readonly formGroupLoggedIn: FormGroup<ContactLoggedInFormType>;
  public readonly formGroupNotLoggedIn: FormGroup<ContactNotLoggedInFormType>;
  public clouseExpanded: WritableSignal<boolean> = signal(false);
  private readonly _angulartics2GoogleTagManager = inject(Angulartics2GoogleTagManager);

  constructor(
    private readonly _toastrService: ToastrService,
    private readonly _formErrorHandlerService: FormErrorHandlerService,
    private readonly _formBuilder: FormBuilder,
  ) {
    super();
    this.formGroupLoggedIn = this._formBuilder.group({
      message: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(1000)]),
      subject: new FormControl<number | null>(null, [Validators.required]),
    });
    this.formGroupNotLoggedIn = this._formBuilder.group({
      message: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(1000)]),
      subject: new FormControl<number | null>(null, [Validators.required]),
      saloonCode: new FormControl<string | null>(null, [Validators.maxLength(10)]),
      firstname: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(255)]),
      lastname: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(255)]),
      phone: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(15)]),
      email: new FormControl<string | null>(null, [Validators.required, Validators.email, Validators.maxLength(255)]),
      zipCode: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(10)]),
    });
  }

  public ngOnInit(): void {
    this._store.dispatch(new ContactStateGetContactSubjects());
  }

  get fLoggedIn() {
    return this.formGroupLoggedIn.controls;
  }

  get fNotLoggedIn() {
    return this.formGroupNotLoggedIn.controls;
  }

  @HostListener('document:keydown.enter', ['$event'])
  public onEnterKeydownHandler(event: KeyboardEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.onSubmit();
  }

  public onSubmit(): void {
    const form = this.user() ? this.formGroupLoggedIn : this.formGroupNotLoggedIn;
    form.markAllAsTouched();
    if (form.invalid) {
      this._toastrService.error('Proszę poprawnie wypełnić formularz.', 'Błąd');
      return;
    }

    const action = this.user() ? new ContactStateSubmitMessageLoggedIn() : new ContactStateSubmitMessageNotLoggedIn();
    this._store
      .dispatch(action)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        catchError((error) => {
          if (error === 'timeout') {
            this._toastrService.error('Błąd przekroczenia limitu czasu żądania', 'Błąd');
          }
          this._formErrorHandlerService.catchFormError(error, form);
          if (error?.error?.code === 400) {
            const children = error?.error?.errors?.children;
            for (const key in children) {
              if (children[key]?.errors?.length) {
                this._toastrService.error(children[key]?.errors[0], 'Błąd');
              }
            }
          }
          return throwError(() => error);
        }),
        tap(() => {
          this._toastrService.success('Wiadomość została wysłana.', 'Sukces');

          let subject = '';
          if (this.contactSubjects()?.length) {
            this.contactSubjects()?.length
              ? this.contactSubjects()?.find((cS: IContactSubject) => cS.id === form.controls['subject'].value)?.subject
              : '';
          }

          this._pushLayerOnSuccessfullLogin(subject);
          form.reset();
        }),
      )
      .subscribe();
  }

  private _pushLayerOnSuccessfullLogin(subject: string): void {
    this._angulartics2GoogleTagManager.pushLayer({
      event: 'uaevent',
      ecommerce: 'undefined',
      eventCategory: 'form',
      eventAction: 'submit',
      eventLabel: 'contact us',
      event_name: 'submit_contact_us',
      form_name: 'contact us',
      requestType: subject,
    });
  }
}
